import { DragVisual } from "../components/psychologist/calendar/types";
import { addMinutesToTime, timeToMinutes, toTwo } from "../components/psychologist/calendar/utils";
import { doesOverlapExistingTemplate } from "../components/psychologist/calendar/validation";
import { getDragBlockReason } from "./getDragBlockReason";

export function getWeekdayIndex(dateStr: string, startOfWeek: Date): number {
  const start = new Date(startOfWeek);
  start.setHours(0, 0, 0, 0);

  const [y, m, d] = dateStr.split('-').map(Number);
  const day = new Date(y, m - 1, d);
  day.setHours(0, 0, 0, 0);

  return Math.max(
    0,
    Math.min(6, Math.round((day.getTime() - start.getTime()) / 86400000))
  );
}

export function getTotalMinutesForDay(
  template: { id: string; weekday: number; startTime: string; endTime: string; active: boolean }[],
  weekday: number
): number {
  return template
    .filter(t => t.active && t.weekday === weekday)
    .reduce((sum, t) => {
      const start = timeToMinutes(t.startTime);
      let end = timeToMinutes(t.endTime);
      if (end === 0) end = 24 * 60;
      return sum + (end - start);
    }, 0);
}

interface DragCalcResult {
  startTime: string;
  endTime: string;
  startY: number;
  currentY: number;
  blocked: boolean;
  reason: string | null;
}

export function handleDragDown(params: {
  initStartMin: number;
  pointerMinRaw: number;
  weekdayIdx: number;
  template: { id: string; weekday: number; startTime: string; endTime: string; active: boolean }[];
  dragVisual: DragVisual | null;
  snappedTop: number;
  rowHeightPx: number;
  quarterHeightPx: number;
}) : DragCalcResult {

  const {
    initStartMin,
    pointerMinRaw,
    weekdayIdx,
    template,
    dragVisual,
    snappedTop,
    quarterHeightPx
  } = params;

  let desiredDur = Math.max(60, pointerMinRaw - initStartMin + 30);
  desiredDur = Math.ceil(desiredDur / 30) * 30;
  const existingRanges = template.filter(t => t.active && t.weekday === weekdayIdx);

  let nextStartMin = Infinity;
  for (const t of existingRanges) {
    const tStart = timeToMinutes(t.startTime);
    if (tStart > initStartMin && tStart < nextStartMin) {
      nextStartMin = tStart;
    }
  }

  const maxByDay = Math.max(30, 24 * 60 - initStartMin);
  let maxByTemplate = isFinite(nextStartMin)
    ? Math.max(0, nextStartMin - initStartMin)
    : maxByDay;

  maxByTemplate = Math.floor(maxByTemplate / 30) * 30;

  const clampedDur = Math.max(
    30,
    Math.min(desiredDur, maxByDay, maxByTemplate)
  );

  const overlap = doesOverlapExistingTemplate(
    template,
    weekdayIdx,
    initStartMin,
    initStartMin + clampedDur
  );

  const reason = getDragBlockReason({
    exceedsMaxHours: false,
    crossesMidnight: (initStartMin + clampedDur) > 24 * 60,
    overlaps: overlap
  });

  const endTimeStr = addMinutesToTime(
    `${toTwo(Math.floor(initStartMin / 60))}:${toTwo(initStartMin % 60)}`,
    clampedDur
  );

  const heightQuarters = Math.max(2, Math.ceil(clampedDur / 30));
  const startY = dragVisual ? dragVisual.startY : snappedTop;
  const currentY = startY + heightQuarters * quarterHeightPx;

  return {
    startTime: `${toTwo(Math.floor(initStartMin / 60))}:${toTwo(initStartMin % 60)}`,
    endTime: endTimeStr,
    startY,
    currentY,
    blocked: !!reason,
    reason
  };
}

export function handleDragUp(params: {
  initStartMin: number;
  initDurationMin: number;
  pointerMinRaw: number;
  weekdayIdx: number;
  template: { id: string; weekday: number; startTime: string; endTime: string; active: boolean }[];
  dragVisual: DragVisual | null;
  snappedTop: number;
  quarterHeightPx: number;
}) : DragCalcResult {

  const {
    initStartMin,
    initDurationMin,
    pointerMinRaw,
    weekdayIdx,
    template,
    dragVisual,
    snappedTop,
    quarterHeightPx
  } = params;

  const existingRanges = template.filter(t => t.active && t.weekday === weekdayIdx);

  const candidateStart = Math.floor(Math.max(0, pointerMinRaw) / 30) * 30;

  let prevEndBefore = -Infinity;
  for (const t of existingRanges) {
    const tEnd = timeToMinutes(t.endTime);
    if (tEnd <= initStartMin && tEnd > prevEndBefore) {
      prevEndBefore = tEnd;
    }
  }

  const minStartAllowed = isFinite(prevEndBefore) ? prevEndBefore : 0;
  const newStartMin = Math.max(minStartAllowed, candidateStart);
  const newEndMin = newStartMin + initDurationMin;

  const overlap = doesOverlapExistingTemplate(
    template,
    weekdayIdx,
    newStartMin,
    newEndMin
  );

  const reason = getDragBlockReason({
    exceedsMaxHours: false,
    crossesMidnight: newEndMin > 24 * 60,
    overlaps: overlap
  });

  const heightQuarters = Math.max(2, Math.ceil(initDurationMin / 30));
  const anchorBottomY = dragVisual ? dragVisual.currentY : snappedTop + heightQuarters * quarterHeightPx;
  const startY = anchorBottomY - heightQuarters * quarterHeightPx;

  return {
    startTime: `${toTwo(Math.floor(newStartMin / 60))}:${toTwo(newStartMin % 60)}`,
    endTime: `${toTwo(Math.floor(newEndMin / 60))}:${toTwo(newEndMin % 60)}`,
    startY,
    currentY: anchorBottomY,
    blocked: !!reason,
    reason
  };
}